import React, { useState, useEffect, useRef, useMemo } from 'react';
import { makeStyles } from 'react-scale/components/design-system/styles';
import { useHistory } from 'react-router-dom';
import SelfServeInstructions from './SelfServeInstructions';
import API from '@scale/frontend-lib/dashboard-api';
import { Project } from 'dashboard/types';
import _ from 'lodash';
import { taxonomyToInstructions } from '@scale/shared/selfServe/helpers';

import { TaskType } from '@scale/shared-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Alert from 'design-system/Alert';
import { QueryClient, QueryClientProvider } from 'react-query';
import { IUserJson } from '@scale/shared/types/user';
import qs from 'qs';
// eslint-disable-next-line restrict-imports/restrict-imports
import theme from 'design-system/product/theme';
import { getUrlBase } from 'dashboard/components/SelfServe/helpers';
import Button from 'design-system/ButtonLegacy';
import useUnleashFeatureFlag from 'react-scale/hooks/useUnleashFeatureFlag';
import { isStudioProject } from 'react-scale/studio/helpers';
import { Event, logEvent } from '@scale/frontend-lib/events';

interface SelfServeInstructionsPageProps {
  user?: IUserJson;
  project?: Project;
  instructionObjectId?: string; // if provided we'll be in read only mode
  onSave?: () => void;
  isEdgeCase?: boolean;
  isPreviewImg?: boolean;
  startTasks?: () => void;
  startButtonText?: string;
  isReviewMode?: boolean;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

const MIN_INSTRUCTION_TIME_SECS = 20;
const MAX_EXAMPLES_REQUIRED = 3;

// eslint-disable-next-line complexity
const SelfServeInstructionsPage: React.FC<SelfServeInstructionsPageProps> = ({
  user,
  project,
  instructionObjectId,
  onSave,
  isEdgeCase,
  isPreviewImg = false,
  startTasks,
  startButtonText,
  isReviewMode,
}) => {
  const readOnly = !!instructionObjectId || !project;
  const classes = useStyles();

  const [blocks, setBlocks] = useState<Record<string, any>[]>([
    { type: 'heading-2', children: [{ text: 'Loading...' }] },
  ]);
  const [prevInstructions, setPrevInstructions] = useState<any>(undefined); // used to check if instructions have been modified
  const [loading, setLoading] = useState(true);
  const [disabled, setDisabled] = useState(false);
  const [uploadedAssets, setUploadedAssets] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [infoMsg, setInfoMsg] = useState('');
  const [saved, setSaved] = useState(false);
  const [hasSensitiveContent, setHasSensitiveContent] = useState(false);
  const [feedback, setFeedback] = useState<any>(null);
  const [updatedAt, setUpdatedAt] = useState<string | undefined>(undefined);
  const [feedbackSummary, setFeedbackSummary] = useState<string[] | undefined>(undefined);

  const ref = useRef<HTMLDivElement>(null);
  const showStartTaskButton = startTasks && !!startButtonText;
  const isCorpUser = user?.spoofed || (user?.email && user?.email.endsWith('scale.com'));
  const [hasScrolledInstructions, setHasScrolledInstructions] = useState(false);
  const [hasSeenInstructions, setHasSeenInstructions] = useState(false);
  const [hasViewedExamples, setHasViewedExamples] = useState(false);
  const useInstructionLimits = useUnleashFeatureFlag('use-rapid-instruction-limits', {}, false);
  const [timeStarted, setTimeStarted] = useState(0);
  const [goodExampleCount, setGoodExampleCount] = useState(0);
  const [badExampleCount, setBadExampleCount] = useState(0);
  const [goodExamplesSeen, setGoodExamplesSeen] = useState<string[]>([]);
  const [badExamplesSeen, setBadExamplesSeen] = useState<string[]>([]);

  const [stateBustingKey, setStateBustingKey] = useState(0);

  const enableStartTaskButton = useMemo(
    () =>
      !useInstructionLimits ||
      (hasScrolledInstructions && hasSeenInstructions && hasViewedExamples),
    [useInstructionLimits, hasScrolledInstructions, hasSeenInstructions, hasViewedExamples],
  );

  const [taxonomy, setTaxonomy] = useState(null);
  const history = useHistory();

  const hasOpenedExample = (taskId: string, isGoodExample: boolean) => {
    if (showStartTaskButton) {
      const toModify = isGoodExample ? goodExamplesSeen : badExamplesSeen;
      const setFunction = isGoodExample ? setGoodExamplesSeen : setBadExamplesSeen;
      setFunction([...toModify, taskId]);

      const modifiedArrayComplete =
        toModify.length + 1 >=
        Math.min(MAX_EXAMPLES_REQUIRED, isGoodExample ? goodExampleCount : badExampleCount);

      const otherArrayComplete =
        (isGoodExample ? badExamplesSeen : goodExamplesSeen).length >=
        Math.min(MAX_EXAMPLES_REQUIRED, isGoodExample ? badExampleCount : goodExampleCount);

      if (modifiedArrayComplete && otherArrayComplete) {
        setHasViewedExamples(true);
      }
    }
  };

  const handleStart = () => {
    async function updateUser() {
      if (instructionObjectId) {
        const timeSpentSecs = Math.round((new Date().getTime() - timeStarted) / 1000);
        await API.selfserve.setUserHasSeenInstructions({
          instructionObjectId,
          timeSpentSecs,
          usingInstructionRestrictions: useInstructionLimits,
        });

        const data = {
          isNewInstruction: false,
          isOnboardingFlow: false,
          isDuringTasking: false,
          openTabButtonClicked: false,
          startTime: timeStarted,
          closeTime: new Date().getTime(),
        };

        await logEvent(Event.InstructionRenderRecord, data);
      }
    }

    if (startTasks) {
      startTasks();
      updateUser();
    }
  };

  useEffect(() => {
    setTimeStarted(new Date().getTime());
  }, [setTimeStarted]);

  useEffect(() => {
    if (!hasSeenInstructions && showStartTaskButton) {
      let seconds = MIN_INSTRUCTION_TIME_SECS;

      const timer = setInterval(function () {
        if (seconds === 0) {
          clearInterval(timer);
          setHasSeenInstructions(true);
        }
        seconds -= 1;
      }, 1000);

      return () => {
        clearInterval(timer);
      };
    }
  }, [showStartTaskButton, hasSeenInstructions, instructionObjectId]);

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      let instructionsRes;
      if (instructionObjectId) {
        try {
          instructionsRes = await API.selfserve.fetchProjectInstructions({
            instructionObjectId,
            showChangeDescriptions: showStartTaskButton,
          });
          if (instructionsRes.instructions) {
            if (showStartTaskButton) {
              const hasSeen = await API.selfserve.fetchUserHasSeenInstructions({
                instructionObjectId,
              });

              if (hasSeen) {
                setHasSeenInstructions(hasSeen);
                setHasScrolledInstructions(true);
                setHasViewedExamples(true);
              }
            }
            const instructionsToUse =
              isReviewMode && instructionsRes.reviewInstructions
                ? instructionsRes.reviewInstructions
                : instructionsRes.instructions;

            setBlocks(instructionsToUse);
            setPrevInstructions(instructionsToUse);
            setHasSensitiveContent(instructionsRes.hasSensitiveContent);
            handleScrollDebounced();

            let goodExamples = 0;
            let badExamples = 0;
            instructionsRes.instructions.map(
              (b: any) =>
                _.has(b, 'examples') &&
                b.examples.map((ex: any) => (ex.isGoodExample ? goodExamples++ : badExamples++)),
            );
            setGoodExampleCount(goodExamples);
            setBadExampleCount(badExamples);

            if (goodExamples + badExamples === 0) {
              setHasViewedExamples(true);
            }

            setLoading(false);
            if (instructionsRes.updatedAt) setUpdatedAt(instructionsRes.updatedAt);
          } else {
            setBlocks([
              {
                type: 'heading-2',
                children: [{ text: `ERROR: no instructions exist for ID ${instructionObjectId}` }],
              },
            ]);
            setLoading(false);
          }
        } catch (e) {
          setErrorMsg('failed to find the instructions');
          setLoading(false);
        }
        setStateBustingKey(stateBustingKey + 1);
        return;
      }

      if (!project) {
        alert('ERROR: component must be provided ID of either a Project or InstructionObject');
        return;
      }
      try {
        instructionsRes = await API.selfserve.fetchProjectInstructions({ project: project.id });
      } catch (e) {
        setErrorMsg('failed to find the instructions');
        setLoading(false);
      }

      const { taxonomy: taxonomyRes } = await API.selfserve.fetchProjectTaxonomy({
        project: project.id,
      });
      setTaxonomy(taxonomyRes);

      if (instructionsRes.instructions) {
        setBlocks(instructionsRes.instructions);
        setPrevInstructions(instructionsRes.instructions);
        setHasSensitiveContent(instructionsRes.hasSensitiveContent);
        if (instructionsRes.updatedAt) setUpdatedAt(instructionsRes.updatedAt);
      } else {
        // no instructions exists so create initial instructions from taxonomy
        setBlocks(taxonomyToInstructions(taxonomyRes, project.name, project.type));
      }
      setLoading(false);

      const uploadedAssets = (await API.selfserve.fetchProjectAssets({ project: project.id }))
        .uploadedAssets;

      const labeledExampleAssets = uploadedAssets.filter(
        (uploadedAsset: $FIXME) => !!uploadedAsset.usedForLabeledExample,
      );

      setUploadedAssets(labeledExampleAssets.length > 0 ? labeledExampleAssets : uploadedAssets);
      if (uploadedAssets.length > 0 && project.type === TaskType.DocumentTranscription) {
        // warm cache for the first asset
        API.selfserve.fetchAssetPages({ assetId: uploadedAssets[0]._id });
      }

      setStateBustingKey(stateBustingKey + 1);
    }
    fetchData();
  }, [project, instructionObjectId]);

  useEffect(() => {
    async function fetchData() {
      if (project) {
        const { feedback, score, instructionFeedbackSummary } =
          await API.selfserve.fetchInstructionPageFeedback({
            project: project.id,
          });
        if (feedback.length > 0) {
          setFeedback({ feedback, score });
        }
        if (instructionFeedbackSummary && instructionFeedbackSummary.length > 0) {
          setFeedbackSummary(instructionFeedbackSummary);
        }
      }
    }
    fetchData();
  }, [project]);

  const handleSave = async (
    updateTasks: boolean,
    updateLabelers: boolean = false,
    changeDescription?: string,
  ) => {
    setDisabled(true);
    try {
      const body = {
        project: project!.id,
        instructions: blocks,
        updateTasks,
        updateLabelers,
        ...(changeDescription && { changeDescription }),
      };
      await API.selfserve.setProjectInstructions(body);
      setPrevInstructions(blocks);
      setUpdatedAt(undefined);
      onSave && onSave();

      setSaved(true);
      setTimeout(() => setSaved(false), 5000);
    } catch (e) {
      setErrorMsg('failed to save');
    }
    setDisabled(false);
  };

  const handleScroll = () => {
    if (hasScrolledInstructions) {
      return;
    }

    const container = ref.current;
    if (!container || container.scrollHeight < 1000) {
      setHasScrolledInstructions(true);
      return;
    }

    if (showStartTaskButton && !enableStartTaskButton) {
      const bottom = container.scrollHeight - container.scrollTop - 500 <= container.clientHeight;
      if (bottom) {
        setHasScrolledInstructions(true);
      }
    }
  };

  const handleScrollDebounced = _.debounce(handleScroll, 10);

  const isDirty = !_.isEqual(blocks, prevInstructions);
  const feedbackToDisplay = feedbackSummary || feedback?.feedback || [];
  return (
    <QueryClientProvider client={queryClient}>
      <div className={classes.outerContainer}>
        {showStartTaskButton && (
          <div className={classes.banner}>Read all instructions and view examples to continue!</div>
        )}
        <div className={classes.documentContainer} ref={ref} onScroll={handleScrollDebounced}>
          <div style={{ display: isPreviewImg ? 'block' : 'flex' }}>
            <div className={readOnly ? classes.instructionWrapper : classes.documentWrapper}>
              {infoMsg && (
                <Alert severity="warning" style={{ marginTop: '6px', marginBottom: '6px' }}>
                  {infoMsg}
                </Alert>
              )}
              {errorMsg.length > 0 && (
                <Alert severity="error" style={{ marginTop: '6px', marginBottom: '6px' }}>
                  {errorMsg}
                </Alert>
              )}
              {saved && (
                <Alert severity="success" style={{ marginTop: '6px', marginBottom: '6px' }}>
                  Successfully saved! If applicable, please{' '}
                  <a
                    onClick={() => {
                      history.push(
                        `/${getUrlBase(history.location.pathname)}/quality?` +
                          qs.stringify({
                            project: project!.id,
                            spoof: user?.spoofed ? user._id : undefined,
                          }),
                      );
                    }}
                  >
                    update your quality tasks
                  </a>
                  !
                </Alert>
              )}
              <SelfServeInstructions
                key={stateBustingKey}
                readOnly={readOnly}
                blocks={blocks}
                setBlocks={setBlocks}
                project={project}
                taxonomy={taxonomy}
                uploadedAssets={uploadedAssets}
                hasSensitiveContent={hasSensitiveContent}
                isDirty={isDirty}
                handleSave={
                  !disabled && (isDirty || isStudioProject(project)) && !loading
                    ? handleSave
                    : undefined
                }
                isEdgeCase={isEdgeCase}
                setInfoMsg={setInfoMsg}
                updatedAt={updatedAt}
                hasOpenedExample={hasOpenedExample}
              />
            </div>
            {!readOnly && (
              <div
                style={{
                  background: 'white',
                  borderRadius: 12,
                  width: '21%',
                  marginLeft: 12,
                  paddingTop: 32,
                  paddingLeft: 32,
                  paddingRight: 32,
                  marginTop: 24,
                }}
              >
                {feedback && feedback.feedback ? (
                  <>
                    <div style={{ color: theme.colors.periwinkle100 }}>
                      Feedback from recent calibration batch
                    </div>
                    {feedback.score > -1 && (
                      <div style={{ marginTop: 12 }}>
                        <div style={{ fontWeight: 'bold', marginBottom: 8 }}>
                          Overall Calibration Score:
                        </div>
                        {Array.from(Array(Math.round((5 * feedback.score) / 100))).map(i => {
                          return <FontAwesomeIcon icon="star" color="gold" key={i} />;
                        })}
                        {Array.from(Array(5 - Math.round((5 * feedback.score) / 100))).map(i => {
                          return <FontAwesomeIcon icon="star" color="gray" key={i} />;
                        })}
                        <span style={{ marginLeft: 12, fontWeight: 'bold' }}>
                          {Math.round(feedback.score)}%
                        </span>
                      </div>
                    )}
                    <div style={{ marginTop: 16, fontWeight: 'bold' }}>Feedback:</div>
                    <div style={{ height: '60vh', overflowY: 'scroll' }}>
                      {feedbackToDisplay.map((item: string) => (
                        <li
                          key={item}
                          style={{ marginLeft: 16, paddingBottom: 4, lineHeight: 1.5 }}
                        >
                          {item}
                        </li>
                      ))}
                    </div>
                  </>
                ) : (
                  <p style={{ opacity: 0.75 }}>
                    <strong>Write guidelines for completing your tasks.</strong> <br />
                    To host the instructions for collaborative editing, you can use this&nbsp;
                    <a
                      href="https://docs.google.com/document/d/1fjgRSni7PNzMCSKw7NqVfGAp29phcf3NzrAojUhpVUY/edit"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      template
                    </a>{' '}
                    and then embed it as an iframe.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
        {showStartTaskButton && (
          <div className={classes.buttons}>
            <Button
              disabled={!enableStartTaskButton}
              size="lg"
              primary
              arrows
              onClick={handleStart}
            >
              {enableStartTaskButton
                ? startButtonText
                : !hasSeenInstructions
                ? `Take more time to read the instructions`
                : hasScrolledInstructions
                ? goodExamplesSeen.length < Math.min(goodExampleCount, MAX_EXAMPLES_REQUIRED)
                  ? `View more well-labeled examples`
                  : `View more poorly labeled examples`
                : `Read the entire page`}
            </Button>
            {isCorpUser && (
              <Button size="lg" arrows onClick={startTasks}>
                {'Force Start (corp user)'}
              </Button>
            )}
          </div>
        )}
      </div>
    </QueryClientProvider>
  );
};

const useStyles = makeStyles({
  buttons: {
    marginTop: '10px',
    flexShrink: 0,
    padding: 5,
    display: 'flex',
    gap: 10,
  },
  outerContainer: {
    height: '85vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    overflow: 'hidden',
  },
  documentContainer: {
    overflow: 'auto',
    marginLeft: 24,
  },
  documentWrapper: {
    maxWidth: '68%',
    height: '75vh',
    borderRadius: 12,
    marginTop: 24,
    border: '1px solid #E6E6E6',
    textAlign: 'left',
    paddingTop: 32,
    paddingLeft: 32,
    paddingRight: 32,
    overflow: 'auto',
    background: 'white',
  },
  instructionWrapper: {
    width: '1000px !important',
    overflow: 'auto',
    padding: 32,
    background: 'white',
  },
  banner: {
    textAlign: 'center',
    width: '100%',
    height: '50px',
    background: 'green',
    color: 'white',
    fontSize: '16px',
    padding: '15px',
    fontWeight: 600,
  },
});

export default SelfServeInstructionsPage;
